<template>
  <!-- Modal.vue renders this component AS `.modal_content`, which already owns
       the scroll (overflow-y:auto; max-height:80vh; padding:30px). So the root
       adds no scroll container of its own — one scroll box, one owner.
       tabindex="-1" + the mounted() focus is what makes Esc reach us at all:
       Modal.vue has no Esc handler and no backdrop close.
       The Esc handler deliberately does NOT carry .stop/.prevent as modifiers —
       see onEsc: focus stays in here after the chat modal opens on top, and
       swallowing the event unconditionally would close THIS list while leaving
       the chat behind. -->
  <div
    class="dropoff-modal"
    dir="rtl"
    tabindex="-1"
    ref="root"
    @keydown.esc="onEsc"
  >
    <div class="do-head">
      <div class="do-head-figures">
        <span v-if="hasFigures" class="do-count">
          {{ formatNumber(payloadData.dropped) }} מתוך {{ formatNumber(payloadData.expected) }}
        </span>
        <span v-if="fetchedAtLabel" class="do-stamp">נכון ל-{{ fetchedAtLabel }}</span>
      </div>
      <button type="button" class="chat-refresh" :disabled="loading" @click="load">רענון</button>
    </div>

    <!-- The page's figures moved while this list was open. NOT sticky: the
         scroll box is Modal.vue's padded container, and a sticky child would pin
         to the wrong edge. -->
    <div v-if="isStale" class="do-stale">
      <span>המספרים בעמוד התעדכנו מאז שנפתחה הרשימה (נכון ל-{{ fetchedAtLabel }}).</span>
      <button type="button" class="chat-refresh" :disabled="loading" @click="load">רענון</button>
    </div>

    <!-- Loading: three row skeletons rather than a spinner, so the shape of what
         is coming is already on screen (the chat modal's idiom). -->
    <div v-if="loading">
      <div v-for="n in 3" :key="'skeleton-' + n" class="do-row do-row-skeleton">
        <div v-for="w in skeletonWidths" :key="'sk-' + n + '-' + w" class="do-skeleton-cell">
          <div class="skeleton-line" :style="{ width: w }"></div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="chat-state chat-state-error">
      <p class="chat-state-text">
        לא הצלחנו לטעון את רשימת הנשירה — הבקשה לשרת נכשלה. הנתונים בעמוד עצמם תקינים. נסה שוב.
      </p>
      <button type="button" class="chat-retry" @click="load">נסה שוב</button>
    </div>

    <div v-else-if="!available" class="notice notice-warning">
      רשימת הנשירה אינה זמינה כרגע ({{ unavailableReason }}) — הנתון יחזור כששירות השיחות יחזור.
      אפשר לנסות שוב מאוחר יותר.
    </div>

    <template v-else>
      <div v-if="isTruncated" class="notice notice-warning">
        הנתונים חלקיים — רשימת השיחות נחתכה ב-2,000 שורות, ולכן ייתכן שחסרות שורות.
        אפשר לנסות שוב מאוחר יותר.
      </div>

      <!-- The server answered but declined to compute the list (the same
           `too_many` guard the strip applies): say so, rather than show an empty
           list that would read as "nothing dropped". -->
      <div v-else-if="isTooMany" class="notice notice-warning">
        לא חושב — יותר מ-1,000 הצעות בחלון הזמן, ולכן החישוב הושהה כדי לא להאט את העמוד.
        צמצום חלון הזמן יאפשר חישוב.
      </div>

      <!-- conv-svc answered but returned no rows for this operation. Nothing was
           measured, so an empty list here would be a claim, not a result. -->
      <div v-else-if="isRowsMissing" class="notice notice-warning">
        אין נתון — לא התקבלו שורות עבור הפעילות הזו משירות השיחות, ולכן אי אפשר לדעת
        אילו הצעות היו אמורות להגיע ל-BA. אפשר לנסות שוב מאוחר יותר.
      </div>

      <div v-else-if="!hasAnyRow" class="chat-state">
        <p class="chat-state-text">
          אף הצעה לא נשרה בדרך ל-BA בחלון הזמן — כל ההצעות שהיו אמורות להישלח הגיעו להערכת BA.
          אפשר לסגור את החלון.
        </p>
      </div>

      <!-- Sections in the order the SERVER published them: pipeline failures
           first, then the candidate's own decisions, then the rows rebase cannot
           speak for. The last two are muted — countable, not alarming. -->
      <template v-else-if="hasAnyRow">
        <div v-for="section in sections" :key="section.key" class="do-section-block">
          <div class="do-section" :class="{ 'is-muted': section.muted }">
            <span class="do-section-title">{{ section.title }}</span>
            <span class="do-section-count">({{ formatNumber(section.count) }})</span>
            <span v-if="section.suffix" class="do-section-suffix">{{ section.suffix }}</span>
          </div>

          <div v-for="group in section.groups" :key="group.key" class="do-group-block">
            <div class="do-group">
              <span>{{ group.label_he }}</span>
              <span class="do-group-count">({{ formatNumber(group.count) }})</span>
            </div>

            <template v-for="(row, rowIndex) in groupRows(group)">
              <div :key="rowKey(group, row, rowIndex)" class="do-row" :class="{ 'is-muted': section.muted }">
                <div class="do-cell">
                  <LinkEntity v-if="applicantPayload(row)" :payload="applicantPayload(row)" />
                  <!-- The id shown here is the OFFER's, not the applicant's —
                       an unresolved row has no applicant id to show — so it is
                       labelled as such rather than read as a candidate number. -->
                  <span v-else class="drill-plain">מועמד לא מזוהה{{ offerIdSuffix(row.rebase_offer_id) }}</span>
                </div>
                <div class="do-cell">
                  <LinkEntity v-if="positionPayload(row)" :payload="positionPayload(row)" />
                  <span v-else class="drill-plain">משרה לא מזוהה{{ idSuffix(positionId(row)) }}</span>
                </div>
                <div class="do-cell">
                  <LinkEntity v-if="companyPayload(row)" :payload="companyPayload(row)" />
                  <span v-else class="drill-plain">—</span>
                </div>
                <div class="do-cell do-time">{{ formatShortStamp(row.pitched_at) }}</div>
                <div class="do-cell">
                  <span v-if="row.bucket" class="drill-tag" :class="'drill-tag-' + row.bucket">
                    {{ row.bucket_label_he }}
                  </span>
                  <p v-if="row.evidence" class="do-evidence">{{ row.evidence }}</p>
                </div>
                <div class="do-cell">
                  <!-- The conversation opens in ChatbotConversationModal, the
                       same one the funnel page's drilldowns use. Modals are a
                       stacked array in the store, so it renders OVER this list.
                       Focus stays in this component though, which is why the
                       root's Esc handler has to check the stack (onEsc) instead
                       of closing this list outright. -->
                  <button
                    v-if="canOpenChat(row)"
                    type="button"
                    class="drill-chat-btn drill-chat-btn-row"
                    title="הצגת השיחה המלאה עם הצ'אטבוט"
                    @click="openChatModal(row)"
                  >צ'אט 💬</button>
                  <span v-else class="drill-plain">אין שיחה מקושרת</span>
                </div>
              </div>
            </template>
          </div>
        </div>

        <p v-if="footnote" class="do-footnote">{{ footnote }}</p>
      </template>
    </template>
  </div>
</template>

<script>
// The list behind the pipeline funnel's 8->9 drop-off strip.
//
// Data is FROZEN on open: the funnel page keeps polling every 60s, and a list
// that reshuffled under the reader's cursor would be unusable. When the page's
// figures move, a stale banner appears and the reader refreshes deliberately.
import { mapGetters, mapMutations } from 'vuex'
import LinkEntity from '@/components/LinkEntity'

// The three muted/plain section headers, keyed by the SERVER's section value —
// the taxonomy itself is server-driven, this table only names the three buckets.
const SECTION_TITLES = {
  pipeline: 'כשלי צינור',
  candidate_decision: 'החלטת מועמד',
  no_rebase_offer: 'ללא מועמדות ברבייס'
}
const SECTION_ORDER = ['pipeline', 'candidate_decision', 'no_rebase_offer']

// Kept in step with the same table in PipelineFunnel.vue: a technical slug is the
// server's vocabulary, not the reader's.
const REASON_LABELS = {
  conv_svc_unreachable: 'אין חיבור לשירות השיחות',
  conv_svc_timeout: 'שירות השיחות לא ענה בזמן',
  conv_svc_breaker_open: 'שירות השיחות מושבת זמנית לאחר כשלים חוזרים',
  conv_svc_request_aborted: 'הבקשה הופסקה אחרי כשל קודם באותה טעינה',
  conv_svc_unexpected_body: 'שירות השיחות החזיר תשובה לא צפויה',
  conversation_service_url_not_configured: 'כתובת שירות השיחות אינה מוגדרת',
  panel_credentials_not_configured: 'פרטי הגישה לשירות השיחות אינם מוגדרים',
  panel_login_failed: 'ההתחברות לשירות השיחות נכשלה',
  rows_missing: 'לא התקבלו שורות עבור הפעילות הזו',
  pitched_rows_invalid: 'התקבלו נתונים לא תקינים משירות השיחות'
}
const MUTED_SECTIONS = ['candidate_decision', 'no_rebase_offer']

export default {
  name: 'FunnelDropOffModal',
  components: {
    LinkEntity
  },
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      error: '',
      response: null,
      skeletonWidths: ['80%', '70%', '50%', '60%', '90%', '40%']
    }
  },
  computed: {
    resourceData () {
      return this.payload.resource_data || {}
    },
    hours () {
      return this.resourceData.hours || 24
    },
    // The strip figures as they were when the modal opened — used to tell a
    // stale list from a fresh one without re-reading the page's state.
    openedWith () {
      return this.resourceData.summary || {}
    },
    payloadData () {
      return this.response || {}
    },
    hasFigures () {
      return typeof this.payloadData.dropped === 'number' &&
        typeof this.payloadData.expected === 'number'
    },
    available () {
      return this.payloadData.available !== false
    },
    // The server's slug in the reader's language; an unknown slug falls through
    // to itself rather than to a blank.
    unavailableReason () {
      const slug = String(this.payloadData.reason || '').trim()
      if (!slug) return 'לא צוינה סיבה'
      if (REASON_LABELS[slug]) return REASON_LABELS[slug]
      if (slug.indexOf('conv_svc_http_') === 0) return 'שירות השיחות החזיר שגיאה'
      return slug
    },
    isTruncated () {
      return this.payloadData.truncated === true
    },
    isTooMany () {
      return this.payloadData.hidden === true && this.payloadData.hidden_reason === 'too_many'
    },
    isRowsMissing () {
      return this.payloadData.hidden === true && this.payloadData.hidden_reason === 'rows_missing'
    },
    groups () {
      return Array.isArray(this.payloadData.groups) ? this.payloadData.groups : []
    },
    hasAnyRow () {
      return this.groups.some(group => this.groupRows(group).length > 0)
    },
    // Groups folded into their server-declared sections, keeping the server's
    // order inside each one. A section with no rows at all is dropped; a section
    // whose rows are all zero-count keeps its header, because "0 candidate
    // decisions" is information.
    sections () {
      const buckets = {}
      this.groups.forEach(group => {
        const key = SECTION_ORDER.indexOf(group.section) === -1 ? 'pipeline' : group.section
        if (!buckets[key]) buckets[key] = []
        buckets[key].push(group)
      })
      return SECTION_ORDER.filter(key => buckets[key]).map(key => {
        const groups = buckets[key]
        const count = groups.reduce((sum, group) => sum + (group.count || 0), 0)
        return {
          key: key,
          title: SECTION_TITLES[key],
          count: count,
          muted: MUTED_SECTIONS.indexOf(key) !== -1,
          suffix: key === 'no_rebase_offer' ? '— אינן נכללות במכנה' : '',
          groups: groups.filter(group => this.groupRows(group).length > 0)
        }
      }).filter(section => section.groups.length > 0 || section.key === 'candidate_decision')
    },
    footnote () {
      const count = this.payloadData.no_rebase_offer
      if (typeof count !== 'number' || count <= 0) return ''
      return 'ללא מועמדות ברבייס: ' + this.formatNumber(count) + ' (אינן נכללות במכנה)'
    },
    fetchedAtLabel () {
      const stamp = this.payloadData.fetched_at || this.payloadData.generated_at
      if (!stamp) return ''
      const parsed = this.$moment(stamp)
      return parsed.isValid() ? parsed.format('HH:mm') : ''
    },
    // The page's strip has moved since this list was taken.
    isStale () {
      if (!this.hasFigures) return false
      const opened = this.openedWith
      if (typeof opened.dropped !== 'number') return false
      return opened.dropped !== this.payloadData.dropped ||
        opened.expected !== this.payloadData.expected
    },
    ...mapGetters('modals', ['findModal'])
  },
  mounted () {
    // Also satisfies "focus moves to the modal header on open".
    if (this.$refs.root && this.$refs.root.focus) this.$refs.root.focus()
    this.load()
  },
  methods: {
    ...mapMutations('modals', ['create', 'remove', 'clearDirtyGuard']),
    // Esc is handled HERE only while this modal is the top of the stack. Once
    // the chat modal is pushed on top, focus is still inside this component (the
    // chat never takes it), so an unconditional handler would close the list and
    // leave the chat orphaned. Letting the event bubble instead hands it to
    // Modals.vue's window listener, which pops the LAST modal — the chat.
    onEsc (event) {
      const modals = (this.$store.state.modals && this.$store.state.modals.modals) || []
      const index = modals.findIndex(modal => modal && modal.modal_id === this.payload.modal_id)
      if (index !== -1 && index < modals.length - 1) return
      event.stopPropagation()
      event.preventDefault()
      this.closeSelf()
    },
    // Mirrors Modal.vue#forceClose exactly: `remove` no-ops while is_fixed is
    // true, so the flag has to be cleared first.
    closeSelf () {
      const payload = this.payload
      if (!payload) return
      payload.is_fixed = false
      this.clearDirtyGuard(payload.modal_id)
      this.remove(payload)
    },
    load () {
      this.loading = true
      this.$network_utils
        .getPipelineFunnelDropOff({ hours: this.hours })
        .then(res => {
          if (!res || typeof res !== 'object') {
            this.error = 'התקבלה תשובה לא צפויה מהשרת'
            return
          }
          this.response = res
          this.error = ''
        })
        .catch(() => {
          this.error = 'שגיאה בטעינת רשימת הנשירה'
        })
        .finally(() => {
          this.loading = false
        })
    },
    groupRows (group) {
      return group && Array.isArray(group.rows) ? group.rows : []
    },
    rowKey (group, row, index) {
      return group.key + '-' + (row.rebase_offer_id || 'x') + '-' + index
    },
    positionId (row) {
      return row && row.position ? row.position.id : null
    },
    // A row that resolves to nothing is never dropped — it renders as plain text
    // with the raw id, so the reader can look it up by hand.
    idSuffix (id) {
      return typeof id === 'number' && id > 0 ? ' (#' + id + ')' : ''
    },
    // Same, but names WHICH id it is: the applicant cell of an unresolved row can
    // only offer the offer's number, and an unlabelled "#77777777" there reads as
    // a candidate id.
    offerIdSuffix (id) {
      return typeof id === 'number' && id > 0 ? ' (הצעה #' + id + ')' : ''
    },
    applicantPayload (row) {
      const applicant = row && row.applicant
      if (!applicant || !applicant.resource_id) return null
      const parts = String(applicant.full_name || '').split(' ')
      return {
        resource_type: 'Applicant',
        resource_id: applicant.resource_id,
        first_name: applicant.first_name || parts[0] || '',
        last_name: applicant.last_name || parts.slice(1).join(' ') || '',
        full_name: applicant.full_name || 'מועמד',
        phone_number: applicant.phone_number || ''
      }
    },
    positionPayload (row) {
      const position = row && row.position
      if (!position || !position.id) return null
      const companyId = row.company && row.company.resource_id
        ? row.company.resource_id
        : position.company_id || null
      return {
        resource_type: 'Position',
        id: position.id,
        resource_id: position.id,
        title: position.title || ('משרה #' + position.id),
        is_hot: position.is_hot,
        is_discrete: position.is_discrete,
        company: { resource_id: companyId }
      }
    },
    companyPayload (row) {
      const company = row && row.company
      if (!company || !company.resource_id) return null
      return {
        resource_type: 'Company',
        resource_id: company.resource_id,
        name: company.name || ('חברה #' + company.resource_id)
      }
    },
    // ---- chatbot conversation ---------------------------------------------
    // Either handle is enough: the row's own conv-svc conversation id, or the
    // applicant id the modal can resolve a conversation from.
    canOpenChat (row) {
      return !!(row && (row.conversation_id || (row.applicant && row.applicant.resource_id)))
    },
    applicantName (row) {
      const applicant = (row && row.applicant) || {}
      const joined = [applicant.first_name, applicant.last_name].filter(Boolean).join(' ').trim()
      return applicant.full_name || joined || 'מועמד לא מזוהה'
    },
    // Opened OVER this modal: store/modules/modals/mutations.js#create pushes
    // onto an array, so the stack renders both and closing the chat returns the
    // reader to this list.
    openChatModal (row) {
      if (!this.canOpenChat(row)) return
      // `create` is a plain push with no dedup: chat on row A then row B would
      // stack two chat modals at the same position. Close the open one first,
      // the way LinkEntity/ButtonEvaluate do for their singleton modals.
      this.closeOpenChatModal()
      this.create({
        designator: 'chatbot-conversation-modal',
        component_name: 'ChatbotConversationModal',
        modal_title: 'שיחה עם הצ\'אטבוט · ' + this.applicantName(row),
        modal_width: 700,
        is_center_modal: true,
        resource_data: {
          conversation_id: row.conversation_id || null,
          applicant: row.applicant || null,
          position_title: row.position && row.position.title
        }
      })
    },
    // is_fixed first, for the same reason closeSelf clears it: `remove` no-ops
    // while the flag is set.
    closeOpenChatModal () {
      const open = this.findModal({ type: 'designator', value: 'chatbot-conversation-modal' })
      if (!open) return
      open.is_fixed = false
      this.remove(open)
    },
    formatNumber (value) {
      return typeof value === 'number' ? value.toLocaleString('he-IL') : value
    },
    formatShortStamp (iso) {
      if (!iso) return ''
      const stamp = this.$moment(iso)
      return stamp.isValid() ? stamp.format('DD/MM HH:mm') : ''
    }
  }
}
</script>

<style scoped>
/* Modal.vue renders this component as `.modal_content`, which already scrolls
   and is already padded — so the root must add neither. */
.dropoff-modal {
  max-height: none;
  overflow: visible;
  padding: 0;
  font-size: 13px;
  color: #334155;
  text-align: right;
}
.dropoff-modal:focus {
  outline: none;
}
.dropoff-modal:focus-visible {
  outline: 2px solid #93c5fd;
  outline-offset: 2px;
}

/* ---- head ---- */
.do-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #eef2f7;
}
.do-count {
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
}
.do-stamp {
  font-size: 12px;
  color: #6b7280;
  margin-inline-start: 8px;
}

/* ---- stale banner (static, never sticky) ---- */
.do-stale {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  margin-bottom: 12px;
}

/* ---- sections / groups ---- */
.do-section {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}
.do-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}
.do-section-count,
.do-section-suffix {
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  margin-inline-start: 6px;
}
/* Muted = countable, not alarming. One full step down, still above 4.5:1 — the
   11px note colour would not be. */
.do-section.is-muted .do-section-title {
  color: #64748b;
}
.do-group {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-top: 10px;
}
.do-group-count {
  font-weight: 400;
  color: #6b7280;
  margin-inline-start: 6px;
}

/* ---- rows ---- */
/* Every track is shrinkable (minmax(0, …)), so nothing can overflow at any
   modal width. The 1040px query below is a readability break, not a guard. */
.do-row {
  display: grid;
  grid-template-columns:
    minmax(0, 1.1fr) minmax(0, 1.6fr) minmax(0, 0.7fr)
    minmax(0, 0.8fr) minmax(0, 2fr) minmax(0, 0.6fr);
  gap: 6px 10px;
  padding: 7px 6px;
  border-bottom: 1px solid #f8fafc;
  align-items: start;
  font-size: 12px;
}
.do-row.is-muted {
  color: #6b7280;
}
.do-cell {
  min-width: 0;
  overflow-wrap: anywhere;
}
.do-time {
  font-size: 11px;
  color: #6b7280;
  direction: ltr;
  unicode-bidi: embed;
  white-space: nowrap;
}
.do-evidence {
  margin: 3px 0 0;
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.do-footnote {
  margin-top: 12px;
  font-size: 11px;
  color: #94a3b8;
}
.do-row-skeleton {
  border-bottom: 1px solid #f8fafc;
}
.do-skeleton-cell {
  min-width: 0;
}
@media (max-width: 1040px) {
  .do-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}

/* ---- copied verbatim: scoped styles do not cascade ---- */
/* from ChatbotConversationModal.vue */
.chat-state {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 12px;
  padding: 28px 16px;
  text-align: center;
}
.chat-state-error {
  background: #fef6f6;
  border-color: #f7dcdc;
}
.chat-state-text {
  margin: 0 0 10px;
  font-size: 13px;
  color: #6b7280;
}
.chat-state-error .chat-state-text {
  color: #b91c1c;
}
.chat-retry {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  padding: 5px 16px;
  font-size: 12px;
  font-family: inherit;
  color: #1f2937;
  cursor: pointer;
}
.chat-retry:hover {
  background: #f3f4f6;
}
.skeleton-line {
  height: 9px;
  border-radius: 5px;
  margin-bottom: 6px;
  background: linear-gradient(90deg, #eceff3 25%, #f6f8fa 50%, #eceff3 75%);
  background-size: 240px 100%;
  animation: chat-shimmer 1.2s infinite linear;
}
@keyframes chat-shimmer {
  0% { background-position: 240px 0; }
  100% { background-position: -240px 0; }
}
.chat-refresh {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 999px;
  padding: 4px 14px;
  font-size: 12px;
  font-family: inherit;
  color: #4b5563;
  cursor: pointer;
}
.chat-refresh:hover:not(:disabled) {
  background: #f3f4f6;
}
.chat-refresh:disabled {
  opacity: 0.6;
  cursor: default;
}

/* from PipelineFunnel.vue */
.notice {
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 16px;
}
.notice-warning {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
}
.drill-plain {
  color: #94a3b8;
}
.drill-tag {
  display: inline-block;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 11px;
  color: #fff;
  white-space: nowrap;
}
.drill-tag-self_applied {
  background: #64748b;
}
.drill-tag-ours_wanted {
  background: #16a34a;
}
.drill-tag-ours_declined {
  background: #dc2626;
}
.drill-tag-inherited {
  background: #e2e8f0;
  color: #475569;
}
.drill-tag-ours_undecided {
  background: #cbd5e0;
  color: #475569;
}
.drill-chat-btn {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  border-radius: 999px;
  padding: 3px 12px;
  font-size: 12px;
  font-family: inherit;
  color: #1e3a8a;
  cursor: pointer;
}
.drill-chat-btn:hover {
  background: #dbeafe;
}
.drill-chat-btn-row {
  display: inline-block;
  margin-top: 0;
  margin-inline-start: 0;
}

/* focus ring — one idiom on every new interactive element */
.chat-refresh:focus-visible,
.chat-retry:focus-visible,
.drill-chat-btn:focus-visible {
  outline: 2px solid #93c5fd;
  outline-offset: 2px;
}
</style>
